import { Dialog } from '@base-ui/react'
import { useMemo } from 'react'
import { z } from 'zod'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { DataTable } from '@/components/ui/data-table'
import { DialogTrigger } from '@/components/ui/dialog'
import { AlertCircleIcon, SearchLgIcon } from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import { formatDate } from '@/dates'
import { EmptyStateListings } from '@/features/luxe-submissions/components/empty-state-listings'
import { FilterLocationSelect } from '@/features/luxe-submissions/components/filter-location-select'
import { FinalScoreBadge } from '@/features/luxe-submissions/components/final-score-badge'
import { InventoryFilterSelect } from '@/features/luxe-submissions/components/inventory-filter-select'
import { MMAskSecondReviewDialog } from '@/features/luxe-submissions/components/mm-ask-second-review-dialog'
import { MMGenerateQuoteDialog } from '@/features/luxe-submissions/components/mm-generate-quote-dialog'
import { MMListingActivityDialog } from '@/features/luxe-submissions/components/mm-listing-activity-dialog'
import { MMParkingLotActionsDropdown } from '@/features/luxe-submissions/components/mm-parking-lot-actions-dropdown'
import { MMParkingLotStatusFilterSelect } from '@/features/luxe-submissions/components/mm-parking-lot-status-filter-select'
import { MMStatusBadge } from '@/features/luxe-submissions/components/mm-status-badge'
import { SubmissionTypeFilterSelect } from '@/features/luxe-submissions/components/submission-type-filter-select'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import { useGetMMParkingLotListings } from '@/features/luxe-submissions/queries/use-get-mm-parking-lot'
import {
	MMParkingLotStatusFilter,
	type MMSubmission,
	MMSubmissionInventory,
	type SelectedMMListing,
	SubmissionType
} from '@/features/luxe-submissions/types'
import {
	mmInventoryToBadgeStyle,
	mmInventoryToLabelTKey,
	mmParkingLotStatusFilterToParams,
	submissionTypeToLabelTKey
} from '@/features/luxe-submissions/utils'
import type { Order, OrderDirection } from '@/helpers/types'
import { cn, debounce } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import {
	ROW_SELECTION_COLUMN_ID,
	useRowSelection
} from '@/hooks/use-row-selection'
import { useRowsPerPage } from '@/hooks/use-rows-per-page'
import { useTranslation } from '@/translations/i18n'

const MMParkingLotSearchParamsSchema = z.object({
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	locationId: z.string().optional(),
	submissionType: SubmissionType.optional(),
	listingInventory: MMSubmissionInventory.optional(),
	// Old links can still carry the removed ready_for_inspection filter
	status: MMParkingLotStatusFilter.optional().catch(undefined),
	search: z.string().optional(),
	page: z.string().optional(),
	perPage: z.string().optional()
})

type SortColumn =
	| 'listingId'
	| 'location'
	| 'submissionType'
	| 'listingInventory'
	| 'score'
	| 'status'

const generateQuoteHandle = Dialog.createHandle<{
	listings: SelectedMMListing[]
}>()

const listingActivityHandle = Dialog.createHandle<{
	listingId: string
}>()

const askSecondReviewHandle = Dialog.createHandle<{
	submissionIds: string[]
}>()

function LuxeSubmissionsMMParkingLot() {
	const { t } = useTranslation()

	const { searchParams, setSearchParams } = useParsedSearchParams(
		MMParkingLotSearchParamsSchema
	)
	const currentPage = searchParams.page ? Number(searchParams.page) : 1
	const { rowsPerPage, setRowsPerPage, totalRows, reportTotal } =
		useRowsPerPage({
			searchParam: searchParams.perPage,
			setSearchParam: (newPerPage) =>
				setSearchParams({ perPage: newPerPage, page: undefined })
		})
	// Queries
	const { data: listingsData, isLoading: isLoadingListings } =
		useGetMMParkingLotListings({
			page: currentPage,
			perPage: rowsPerPage,
			sortBy: searchParams.sortBy,
			sortOrder: searchParams.sortOrder,
			location: searchParams.locationId,
			submissionType: searchParams.submissionType,
			listingInventory: searchParams.listingInventory,
			...(searchParams.status &&
				mmParkingLotStatusFilterToParams[searchParams.status]),
			search: searchParams.search
		})

	// Row selection
	const {
		rowSelectionColumn,
		selectionCount,
		selectedItems,
		resetSelection
	} = useRowSelection<MMSubmission, 'id'>({
		idField: 'id',
		data: listingsData?.data,
		isSelectable: (listing) =>
			listing.mmStatus !== 'back_in_review' &&
			listing.mmStatus !== 'ready_for_inspection',
		getDisabledReason: (listing) => {
			if (listing.mmStatus === 'back_in_review') {
				return t(
					'luxe_submissions:back_in_review_select_disabled_reason'
				)
			}
			if (listing.mmStatus === 'ready_for_inspection') {
				return t(
					'luxe_submissions:ready_for_inspection_select_disabled_reason'
				)
			}
			return undefined
		}
	})

	// Check if all selected rows have the same status
	const selectedStatuses = useMemo(
		() => new Set(selectedItems.map((l) => l.mmStatus)),
		[selectedItems]
	)

	const hasMixedStatuses = selectedStatuses.size > 1

	// Listings the backend can't quote (e.g. missing listing info) are left
	// out of the bulk generate action, but not the other bulk actions
	const quotableItems = useMemo(
		() => selectedItems.filter((l) => l.canGenerateQuote !== false),
		[selectedItems]
	)
	const quoteUnavailableCount = selectedItems.length - quotableItems.length

	// Only one second review is allowed, so listings that already had one are
	// left out of the bulk ask; the backend would skip them anyway
	const secondReviewableIds = useMemo(
		() => selectedItems.filter((l) => !l.hadSecondReview).map((l) => l.id),
		[selectedItems]
	)
	const alreadyReviewedCount =
		selectedItems.length - secondReviewableIds.length

	// Pagination
	const lastPage = listingsData ? listingsData.meta.lastPage : undefined
	reportTotal(listingsData?.meta.total)

	const setPage = (page: number) => {
		setSearchParams({
			page: page === 1 ? undefined : String(page)
		})
	}

	// Filters
	const handleLocationChange = (locationId: string | null) => {
		setSearchParams({
			locationId: locationId ?? undefined,
			page: undefined
		})
	}

	const handleInventoryChange = (listingInventory: string | null) => {
		setSearchParams({
			listingInventory:
				(listingInventory as MMSubmissionInventory) ?? undefined,
			page: undefined
		})
	}

	const handleSubmissionTypeChange = (submissionType: string | null) => {
		setSearchParams({
			submissionType: (submissionType as SubmissionType) ?? undefined,
			page: undefined
		})
	}

	const handleStatusChange = (status: string | null) => {
		setSearchParams({
			status: (status as MMParkingLotStatusFilter | null) ?? undefined,
			page: undefined
		})
	}

	// Debounced search by ID and host email
	const debouncedSearch = useMemo(
		() =>
			debounce((value: string) => {
				setSearchParams({
					search: value || undefined,
					page: undefined
				})
			}, 300),
		[setSearchParams]
	)

	// Order state for DataTable
	const order = useMemo((): Order<SortColumn> | undefined => {
		if (!searchParams.sortBy) {
			return undefined
		}
		return {
			orderBy: searchParams.sortBy as SortColumn,
			orderDirection: searchParams.sortOrder as OrderDirection | undefined
		}
	}, [searchParams.sortBy, searchParams.sortOrder])

	const setOrder = (newOrder?: Order<SortColumn>) => {
		setSearchParams({
			sortBy: newOrder?.orderBy,
			sortOrder: newOrder?.orderDirection,
			page: undefined
		})
	}

	const hasFilters = Object.values(searchParams).some(Boolean)

	// Table columns
	const { isEnabled: isFinalScoreEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-final-score-display'
	)

	const columns = useMemo(
		() => [
			rowSelectionColumn,
			{
				id: 'listingId' as const,
				header: t('luxe_submissions:listing_id_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) => (
					<div className='flex flex-col'>
						<a
							className={cn(
								'truncate font-medium text-neutral-900 text-sm',
								dataItem.listingUrl && 'underline'
							)}
							href={dataItem.listingUrl ?? undefined}
							rel='noopener noreferrer'
							target='_blank'
							title={dataItem.airbnbListingId ?? dataItem.id}
						>
							#{dataItem.airbnbListingId ?? dataItem.id}
						</a>

						{dataItem.listingName && (
							<span
								className='truncate text-neutral-600 text-sm'
								title={dataItem.listingName}
							>
								{dataItem.listingName}
							</span>
						)}
					</div>
				),
				size: 240,
				sortKey: 'listingId' as const
			},
			{
				id: 'hostInfo' as const,
				header: t('luxe_submissions:host_info_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) => (
					<div className='flex flex-col'>
						{dataItem.externalHostId && (
							<span
								className={cn(
									'font-medium text-neutral-900 text-sm'
								)}
							>
								#{dataItem.externalHostId}
							</span>
						)}
						{dataItem.hostEmail && (
							<span
								className='truncate text-neutral-600 text-sm'
								title={dataItem.hostEmail}
							>
								{dataItem.hostEmail}
							</span>
						)}
					</div>
				),
				size: 240
			},
			{
				id: 'location' as const,
				header: t('luxe_submissions:location_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) =>
					dataItem.location,
				size: 160,
				sortKey: 'location' as const
			},
			{
				id: 'submissionType' as const,
				header: t('luxe_submissions:submission_type_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) =>
					dataItem.submissionType ? (
						<span className='text-neutral-700 text-sm'>
							{t(
								submissionTypeToLabelTKey[
									dataItem.submissionType
								]
							)}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					),
				size: 210,
				sortKey: 'submissionType' as const
			},
			{
				id: 'submittedAt' as const,
				header: t('luxe_submissions:submitted_at_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) =>
					dataItem.queueEnteredAt === null ? (
						<span className='text-neutral-500 text-sm'>—</span>
					) : (
						<span className='text-neutral-700 text-sm'>
							{formatDate(dataItem.queueEnteredAt, 'PP')}
						</span>
					),
				size: 165
			},
			{
				id: 'listingInventory' as const,
				header: t('luxe_submissions:inventory_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) =>
					dataItem.listingInventory && (
						<span
							className={`inline-flex rounded-full border px-2 py-0.5 font-medium text-xs ${mmInventoryToBadgeStyle[dataItem.listingInventory]}`}
						>
							{t(
								mmInventoryToLabelTKey[
									dataItem.listingInventory
								]
							)}
						</span>
					),
				size: 260,
				sortKey: 'listingInventory' as const
			},
			{
				id: 'score' as const,
				header: t(
					isFinalScoreEnabled
						? 'luxe_submissions:final_score_column'
						: 'luxe_submissions:score_column'
				),
				cell: ({ dataItem }: { dataItem: MMSubmission }) => (
					<FinalScoreBadge
						blockersFailedCount={dataItem.blockersFailedCount}
						designScore={dataItem.score}
						finalScore={dataItem.finalScore}
					/>
				),
				size: 160,
				sortKey: 'score' as const
			},
			{
				id: 'status' as const,
				header: t('luxe_submissions:status_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) => (
					<MMStatusBadge
						hadSecondReview={dataItem.hadSecondReview}
						status={dataItem.mmStatus}
					/>
				),
				size: 300,
				sortKey: 'status' as const
			},
			{
				id: 'action' as const,
				header: '',
				cell: ({ dataItem }: { dataItem: MMSubmission }) => (
					<div className='flex w-full justify-end'>
						<MMParkingLotActionsDropdown
							askSecondReviewHandle={askSecondReviewHandle}
							dataItem={dataItem}
							generateQuoteHandle={generateQuoteHandle}
							listingActivityHandle={listingActivityHandle}
						/>
					</div>
				),
				size: 60
			}
		],
		[t, rowSelectionColumn, isFinalScoreEnabled]
	)

	return (
		<div className='flex flex-1 flex-col overflow-hidden px-10'>
			{/* Filters */}
			<div className='flex shrink-0 items-center justify-between gap-3 py-6'>
				<div className='flex min-w-0 items-center gap-3'>
					<FilterLocationSelect
						onChange={handleLocationChange}
						selectedId={searchParams.locationId ?? null}
					/>

					<SubmissionTypeFilterSelect
						onChange={handleSubmissionTypeChange}
						value={searchParams.submissionType ?? null}
					/>

					<InventoryFilterSelect
						onChange={handleInventoryChange}
						value={searchParams.listingInventory ?? null}
					/>

					<MMParkingLotStatusFilterSelect
						onChange={handleStatusChange}
						value={searchParams.status ?? null}
					/>
				</div>
				<div className='relative w-[320px]'>
					<SearchLgIcon
						className='pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-500'
						size={20}
					/>
					<Input
						className='h-9 w-full rounded-lg border-neutral-300 pl-10 text-sm shadow-xs placeholder:text-neutral-500'
						defaultValue={searchParams.search ?? ''}
						onChange={(e) => debouncedSearch(e.target.value)}
						placeholder={t(
							'luxe_submissions:search_by_listing_id_email'
						)}
					/>
				</div>
			</div>

			{/* Selection bar */}
			{selectionCount > 0 && (
				<div className='mb-4 flex items-center justify-between rounded-lg border border-neutral-200 bg-primary-50 px-6 py-3'>
					<div className='flex items-center gap-4'>
						<span className='font-medium text-neutral-600 text-xs'>
							{selectionCount} Selected
						</span>

						{hasMixedStatuses && (
							<span className='inline-flex items-center gap-1.5 text-danger-600 text-xs'>
								<AlertCircleIcon size={20} />
								{t(
									'luxe_submissions:same_status_selection_warning'
								)}
							</span>
						)}

						{!hasMixedStatuses && quoteUnavailableCount > 0 && (
							<span className='inline-flex items-center gap-1.5 text-danger-600 text-xs'>
								<AlertCircleIcon size={20} />
								{t(
									'luxe_submissions:quote_generation_not_available_selection_warning',
									{ count: quoteUnavailableCount }
								)}
							</span>
						)}

						{!hasMixedStatuses && alreadyReviewedCount > 0 && (
							<span className='inline-flex items-center gap-1.5 text-danger-600 text-xs'>
								<AlertCircleIcon size={20} />
								{t(
									'luxe_submissions:already_had_second_review_selection_warning',
									{ count: alreadyReviewedCount }
								)}
							</span>
						)}
					</div>
					<div className='flex items-center gap-6'>
						<DialogTrigger
							handle={generateQuoteHandle}
							nativeButton={true}
							payload={{
								listings: quotableItems
							}}
							render={
								<ButtonLegacy
									disabled={
										hasMixedStatuses ||
										selectedStatuses.has(
											'failed_standards'
										) ||
										quotableItems.length === 0
									}
									size='sm'
									type='button'
									variant='outline'
								>
									{t('luxe_submissions:generate_quote')}
								</ButtonLegacy>
							}
						/>
						<DialogTrigger
							handle={askSecondReviewHandle}
							nativeButton={true}
							payload={{
								submissionIds: secondReviewableIds
							}}
							render={
								<button
									className='flex cursor-pointer gap-2 font-medium text-primary-700 text-sm disabled:cursor-not-allowed disabled:opacity-50'
									disabled={
										hasMixedStatuses ||
										secondReviewableIds.length === 0
									}
									type='button'
								>
									{t('luxe_submissions:ask_to_review')}
								</button>
							}
						/>

						<button
							className='cursor-pointer font-medium text-neutral-900 text-sm underline'
							onClick={resetSelection}
							type='button'
						>
							{t('common:clear')}
						</button>
					</div>
				</div>
			)}

			{/* Table */}
			<div className='flex-1 overflow-hidden'>
				<DataTable
					columnPinning={{
						left: [ROW_SELECTION_COLUMN_ID],
						right: ['action']
					}}
					columns={columns}
					data={listingsData?.data ?? []}
					emptyComponent={
						<EmptyStateListings
							action={
								hasFilters ? null : (
									<ButtonLegacy
										onClick={() => window.location.reload()}
										variant='secondary'
									>
										{t('common:refresh')}
									</ButtonLegacy>
								)
							}
							description={t(
								hasFilters
									? 'common:empty_table_description'
									: 'luxe_submissions:parking_lot_no_listings_description'
							)}
							title={t(
								hasFilters
									? 'common:empty_table_title'
									: 'luxe_submissions:parking_lot_no_listings_title'
							)}
						/>
					}
					isLoading={isLoadingListings}
					lastPage={lastPage}
					order={order}
					page={currentPage}
					rowsPerPage={rowsPerPage}
					setOrder={setOrder}
					setPage={setPage}
					setRowsPerPage={setRowsPerPage}
					totalRows={totalRows}
				/>
			</div>

			<MMGenerateQuoteDialog
				handle={generateQuoteHandle}
				onSuccess={selectionCount > 0 ? resetSelection : undefined}
			/>
			<MMListingActivityDialog handle={listingActivityHandle} />
			<MMAskSecondReviewDialog
				handle={askSecondReviewHandle}
				onSuccess={selectionCount > 0 ? resetSelection : undefined}
			/>
		</div>
	)
}

export { LuxeSubmissionsMMParkingLot }
